import { NOTICE_CATEGORIES, type ContractRequest, type Inquiry, type MessagingRequest, type NoticeCategory } from '@selfon/domain/browser';
import { useState, type ReactNode } from 'react';
import { api } from '../api';
import { Alert, Chip, Dialog, Empty, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { DECISION_LABEL, dateOnly, dateTime, MODULE_LABEL, MSG_TYPE_LABEL, NOTICE_LABEL, PLAN_LABEL, won } from '../format';

const pendingFirst = <T extends { status: string }>(a: T, b: T) => Number(b.status === 'PENDING') - Number(a.status === 'PENDING');

/** 승인·반려 공통 대화상자. 반려는 사유가 필요하다. */
function DecideDialog({ target, onClose, onApprove, onReject, summary }: {
  target: { mode: 'approve' | 'reject'; title: string } | null; onClose: () => void;
  onApprove: () => Promise<void>; onReject: (reason: string) => Promise<void>; summary: ReactNode;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog title={target?.title ?? ''} open={Boolean(target)} onClose={() => { setReason(''); onClose(); }} danger={target?.mode === 'reject'}
      submitLabel={target?.mode === 'reject' ? '반려' : '승인'} onSubmit={() => (target?.mode === 'reject' ? onReject(reason) : onApprove())}>
      {summary}
      {target?.mode === 'reject' && (
        <Field id="reject-reason" label="반려 사유 (센터에 그대로 보입니다)"><textarea id="reject-reason" rows={3} required minLength={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      )}
    </Dialog>
  );
}

/** 유료계약 심사: 신청한 요금제·모듈만 반영된다(기존 데모의 무조건 Enterprise Pro 버그 수정). */
export function HqContracts() {
  const list = useLoad(() => api.hqContracts(), []);
  const [sel, setSel] = useState<{ r: ContractRequest; mode: 'approve' | 'reject' } | null>(null);
  const [founding, setFounding] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const rows = (list.data?.contracts ?? []).slice().sort(pendingFirst);
  const pending = rows.filter((r) => r.status === 'PENDING').length;

  return (
    <>
      <PageHeader title="유료계약 심사" />
      {done && <Alert kind="ok">{done}</Alert>}
      {list.error && <Alert>{list.error}</Alert>}
      {list.loading && !list.data && <Loading />}
      {list.data && rows.length === 0 && <Empty>신청 내역이 없습니다.</Empty>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">유료계약 신청 목록</caption>
            <thead><tr><th scope="col">신청일</th><th scope="col">센터</th><th scope="col">신청 상품</th><th scope="col">결제수단</th><th scope="col" className="num">월 청구액</th><th scope="col">상태</th><th scope="col">처리</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap small">{dateOnly(r.requestedAt)}</td>
                  <td><strong>{r.centerName}</strong></td>
                  <td>{PLAN_LABEL[r.plan]}{r.addModules.length > 0 && <span className="muted small"> + {r.addModules.map((m) => MODULE_LABEL[m]).join(', ')}</span>}</td>
                  <td className="small">{r.paymentLabel}</td>
                  <td className="num"><strong>{won(r.monthlyFee)}</strong></td>
                  <td><Chip tone={DECISION_LABEL[r.status]!.tone}>{DECISION_LABEL[r.status]!.label}</Chip>{r.rejectReason && <p className="muted small">{r.rejectReason}</p>}</td>
                  <td>{r.status === 'PENDING' ? (
                    <div className="btn-row">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setSel({ r, mode: 'approve' })}>승인</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSel({ r, mode: 'reject' })}>반려</button>
                    </div>
                  ) : <span className="muted small">{r.decidedAt ? dateOnly(r.decidedAt) : '-'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DecideDialog target={sel && { mode: sel.mode, title: `${sel.r.centerName} 계약 ${sel.mode === 'approve' ? '승인' : '반려'}` }} onClose={() => setSel(null)}
        summary={sel && <><p>{PLAN_LABEL[sel.r.plan]} · 월 {won(sel.r.monthlyFee)}{sel.r.cycle === 'YEARLY' ? ' · 연간 결제' : ''} · {sel.r.paymentLabel}</p>{sel.mode === 'approve' && <label><input type="checkbox" checked={founding} onChange={(e) => setFounding(e.target.checked)} /> 초기 센터 혜택 적용(50% 할인 12개월, 10곳까지)</label>}</>}
        onApprove={async () => { await api.approveContract(sel!.r.id, sel!.r.version, founding); setFounding(false); setDone(`${sel!.r.centerName}의 ${PLAN_LABEL[sel!.r.plan]} 계약을 승인했습니다. 센터 요금제·모듈에 바로 반영됐습니다.`); list.reload(); }}
        onReject={async (reason) => { await api.rejectContract(sel!.r.id, sel!.r.version, reason); setDone(`${sel!.r.centerName}의 신청을 반려했습니다.`); list.reload(); }} />
    </>
  );
}

function messagingDetail(r: MessagingRequest) {
  if (r.type === 'SENDER_PROFILE') return <>{r.kakaoChannelId} · 발신번호 {r.senderNumber}</>;
  if (r.type === 'TEMPLATE') return <><strong>{r.templateName}</strong><p className="small template-body">{r.templateBody}</p></>;
  return <>{won(r.creditAmount ?? 0).replace('원', 'P')} 충전</>;
}

/** 알림톡 발신프로필·템플릿 검수·포인트 충전 승인 */
export function HqMessaging() {
  const list = useLoad(() => api.hqMessaging(), []);
  const [sel, setSel] = useState<{ r: MessagingRequest; mode: 'approve' | 'reject' } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const rows = (list.data?.requests ?? []).slice().sort(pendingFirst);
  return (
    <>
      <PageHeader title="알림톡·문자 승인" />
      {done && <Alert kind="ok">{done}</Alert>}
      {list.error && <Alert>{list.error}</Alert>}
      {list.loading && !list.data && <Loading />}
      {list.data && rows.length === 0 && <Empty>신청 내역이 없습니다.</Empty>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">알림톡·문자 신청 목록</caption>
            <thead><tr><th scope="col">신청일</th><th scope="col">센터</th><th scope="col">유형</th><th scope="col">내용</th><th scope="col">상태</th><th scope="col">처리</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap small">{dateOnly(r.requestedAt)}</td>
                  <td><strong>{r.centerName}</strong></td>
                  <td className="nowrap">{MSG_TYPE_LABEL[r.type]}</td>
                  <td>{messagingDetail(r)}</td>
                  <td><Chip tone={DECISION_LABEL[r.status]!.tone}>{DECISION_LABEL[r.status]!.label}</Chip>{r.rejectReason && <p className="muted small">{r.rejectReason}</p>}</td>
                  <td>{r.status === 'PENDING' ? (
                    <div className="btn-row">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setSel({ r, mode: 'approve' })}>승인</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSel({ r, mode: 'reject' })}>반려</button>
                    </div>
                  ) : <span className="muted small">{r.decidedAt ? dateOnly(r.decidedAt) : '-'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DecideDialog target={sel && { mode: sel.mode, title: `${sel.r.centerName} · ${MSG_TYPE_LABEL[sel.r.type]} ${sel.mode === 'approve' ? '승인' : '반려'}` }} onClose={() => setSel(null)}
        summary={sel && <div>{messagingDetail(sel.r)}</div>}
        onApprove={async () => { await api.approveMessaging(sel!.r.id, sel!.r.version); setDone(`${sel!.r.centerName}의 '${MSG_TYPE_LABEL[sel!.r.type]}' 신청을 승인했습니다.`); list.reload(); }}
        onReject={async (reason) => { await api.rejectMessaging(sel!.r.id, sel!.r.version, reason); setDone(`${sel!.r.centerName}의 신청을 반려했습니다.`); list.reload(); }} />
    </>
  );
}

/** 전체 센터 공지(열람 센터 수 집계) + 센터장 1:1 문의 답변 */
export function HqNotices() {
  const notices = useLoad(() => api.notices(), []);
  const inquiries = useLoad(() => api.hqInquiries(), []);
  const [writing, setWriting] = useState(false);
  const [form, setForm] = useState({ category: 'MUST_READ' as NoticeCategory, title: '', body: '' });
  const [answering, setAnswering] = useState<Inquiry | null>(null);
  const [answer, setAnswer] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const qs = (inquiries.data?.inquiries ?? []).slice().sort((a, b) => Number(b.status === 'OPEN') - Number(a.status === 'OPEN'));

  return (
    <>
      <PageHeader title="공지·문의"
        actions={<button type="button" className="btn btn-primary" onClick={() => { setForm({ category: 'MUST_READ', title: '', body: '' }); setWriting(true); }}>공지 작성</button>} />
      {done && <Alert kind="ok">{done}</Alert>}

      <section className="stack" aria-labelledby="hq-notice-h">
        <h2 id="hq-notice-h">전체 센터 공지</h2>
        {notices.error && <Alert>{notices.error}</Alert>}
        {notices.loading && !notices.data && <Loading />}
        {notices.data?.notices.length === 0 && <Empty>게시한 공지가 없습니다.</Empty>}
        {notices.data && notices.data.notices.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">공지 목록</caption>
              <thead><tr><th scope="col">구분</th><th scope="col">제목</th><th scope="col">게시일</th><th scope="col" className="num">열람 센터</th></tr></thead>
              <tbody>
                {notices.data.notices.map((n) => (
                  <tr key={n.id}>
                    <td><Chip tone={NOTICE_LABEL[n.category]!.tone}>{NOTICE_LABEL[n.category]!.label}</Chip></td>
                    <td><details><summary><strong>{n.title}</strong></summary><p className="pre small">{n.body}</p></details></td>
                    <td className="nowrap small">{dateOnly(n.publishedAt)}</td>
                    <td className="num">{n.readCenters ?? 0} / {n.totalCenters ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="stack" aria-labelledby="hq-inq-h">
        <h2 id="hq-inq-h">원장님 1:1 문의 <span className="muted small">· 답변 대기 {qs.filter((q) => q.status === 'OPEN').length}건</span></h2>
        {inquiries.error && <Alert>{inquiries.error}</Alert>}
        {inquiries.data && qs.length === 0 && <Empty>문의가 없습니다.</Empty>}
        <ul className="inquiry-list">
          {qs.map((q) => (
            <li key={q.id} className="card stack">
              <div className="row"><span><strong>{q.title}</strong> <span className="muted small">· {q.centerName} · {dateTime(q.createdAt)}</span></span>
                <Chip tone={q.status === 'OPEN' ? 'warn' : 'ok'}>{q.status === 'OPEN' ? '답변 대기' : '답변 완료'}</Chip></div>
              <p className="pre small">{q.body}</p>
              {q.answer ? <div className="answer"><strong className="small">본사 답변</strong><p className="pre small">{q.answer}</p></div>
                : <div><button type="button" className="btn btn-outline btn-sm" onClick={() => { setAnswer(''); setAnswering(q); }}>답변 작성</button></div>}
            </li>
          ))}
        </ul>
      </section>

      <Dialog wide title="전체 센터 공지 작성" open={writing} onClose={() => setWriting(false)} submitLabel="전체 센터에 게시"
        onSubmit={async () => { await api.publishNotice(form); setDone('모든 센터의 공지에 게시했습니다.'); notices.reload(); }}>
        <Field id="n-cat" label="구분">
          <select id="n-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as NoticeCategory })}>
            {NOTICE_CATEGORIES.map((c) => <option key={c} value={c}>{NOTICE_LABEL[c]!.label}</option>)}
          </select>
        </Field>
        <Field id="n-title" label="제목"><input id="n-title" required minLength={2} maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field id="n-body" label="내용"><textarea id="n-body" rows={6} required minLength={2} maxLength={4000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
      </Dialog>

      <Dialog wide title={`문의 답변 · ${answering?.centerName ?? ''}`} open={Boolean(answering)} onClose={() => setAnswering(null)} submitLabel="답변 등록"
        onSubmit={async () => { await api.answerInquiry(answering!.id, answering!.version, answer); setDone(`${answering!.centerName} 문의에 답변했습니다. (실제 알림톡 발송은 연결 전)`); inquiries.reload(); }}>
        {answering && <div className="quote"><strong>{answering.title}</strong><p className="pre small">{answering.body}</p></div>}
        <Field id="answer" label="답변"><textarea id="answer" rows={6} required minLength={2} maxLength={4000} value={answer} onChange={(e) => setAnswer(e.target.value)} /></Field>
      </Dialog>
    </>
  );
}
